import { isDefined } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../../../lib/analytics";

export interface ShiftTimeProposalMessageData {
  shiftTimeProposalId: string;
  originalStartTime: string;
  originalEndTime: string;
}

/**
 * Check if a string is valid JSON
 */
function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard for shift time proposal message data
 */
function isShiftTimeProposalData(data: unknown): data is ShiftTimeProposalMessageData {
  return (
    typeof data === "object" &&
    isDefined(data) &&
    "shiftTimeProposalId" in data &&
    typeof (data as ShiftTimeProposalMessageData).shiftTimeProposalId === "string" &&
    "originalStartTime" in data &&
    typeof (data as ShiftTimeProposalMessageData).originalStartTime === "string" &&
    "originalEndTime" in data &&
    typeof (data as ShiftTimeProposalMessageData).originalEndTime === "string"
  );
}

/**
 * Parse shift time proposal data from the admin message data field.
 * Returns shiftTimeProposalId, originalStartTime, and originalEndTime if valid.
 */
export function parseShiftTimeProposalMessageData(
  data: string | undefined
): ShiftTimeProposalMessageData | undefined {
  if (!isDefined(data) || data === "") {
    return undefined;
  }

  if (!isValidJson(data)) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(data);

    if (isShiftTimeProposalData(parsed)) {
      return parsed;
    }

    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: new Error("Shift time proposal message data missing required fields"),
      metadata: { data },
    });

    return undefined;
  } catch (error) {
    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: error instanceof Error ? error : new Error("Failed to parse shift time proposal data"),
      metadata: { data },
    });
    return undefined;
  }
}
